import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { SheetAlignmentEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';
import { arrowUpArrowDown, registerIcon, sortDescending } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import {
	ModalFooter as SkapaModalFooter,
	ModalHeader as SkapaModalHeader,
	Prompt as SkapaPrompt,
	Sheet as SkapaSheet
} from '@ingka/modal-webc/passive.js';

import SortListModalEnum from '../../enums/SortListModalEnum.js';
import SortListOptionsEnum from '../../enums/SortListOptionsEnum.js';
import type ISortListLocalizedInformation from '../../types/ISortListLocalizedInformation.js';
import type ISortListTranslations from '../../types/ISortListTranslations.js';
import LabelUtility from '../../utilities/LabelUtility.js';
import SortListCard from '../sort-list-card/SortListCard.js';
import SortListStyle from './SortListStyle.js';

declare global {
	interface HTMLElementTagNameMap {
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'skapa-icon': SkapaIcon;
		'skapa-sheet': SkapaSheet;
		'skapa-modal-footer': SkapaModalFooter;
		'skapa-modal-header': SkapaModalHeader;
		'skapa-prompt': SkapaPrompt;
	}
}

/**
 * Sort List enables the user to choose how to sort a product list.
 *
 * @event change Triggered when the change button is clicked.
 * @event modalOpen Triggered when a modal should be opened.
 * @event modalClose Triggered when a modal should be closed.
 */

export default class SortList extends KompisElement {
	// Theme
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: ISortListLocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: ISortListTranslations | null = null;

	// Selected option
	@property({ type: String })
	public selected: SortListOptionsEnum | null = null;

	// Sheet alignment
	@property({ type: String })
	public sheetAlignment: SheetAlignmentEnum = SheetAlignmentEnum.right;

	// Visible modal.
	@property({ type: String })
	public visibleModal: SortListModalEnum | null = null;

	private sheetElement: SkapaSheet | null = null;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`
			<style>
				${SortListStyle.getStyle()}
			</style>
			<div class="container">${this.getButton()}</div>
		`;
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.closeSheet();
	}
	public override async connectedCallback(): Promise<void> {
		registerIcon(arrowUpArrowDown);
		registerIcon(sortDescending);

		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('skapa-sheet', SkapaSheet);
		this.customElements.define('skapa-modal-footer', SkapaModalFooter);
		this.customElements.define('skapa-modal-header', SkapaModalHeader);
		this.customElements.define('skapa-prompt', SkapaPrompt);
		this.customElements.define('kompis-sort-list-card', SortListCard);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('visibleModal')) {
			if (this.visibleModal === SortListModalEnum.sortListCard) {
				this.openSheet();
			} else {
				this.closeSheet();
			}
		}
		return true;
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation?.translations;
	}

	private getTranslations(): ISortListTranslations {
		const translations = this.translations
			? this.translations
			: this.localizedInformation?.translations;

		return <ISortListTranslations>translations;
	}

	private getButton(): RenderResult {
		const secondaryVariant: ButtonVariant = 'secondary';

		return html`
			<skapa-button
				variant="${secondaryVariant}"
				size="small"
				@click="${() => {
					this.dispatchEvent(
						new CustomEvent('modalOpen', {
							bubbles: true,
							detail: {
								visibleModal: SortListModalEnum.sortListCard,
								triggerElement: this.queryTriggerElement()
							}
						})
					);
				}}"
				a11y-label="${this.getAriaLabel()}"
			>
				<skapa-icon slot="icon" icon="${this.getIcon(this.selected)}"></skapa-icon>
				${LabelUtility.getLabel(
					this.selected === SortListOptionsEnum.default ? null : this.selected,
					this.getTranslations()
				)}
			</skapa-button>
		`;
	}

	private getAriaLabel(): string {
		if (this.selected === null || this.selected === SortListOptionsEnum.default) {
			return ''; // use label instead of aria label
		}
		const sortOptionLabel = LabelUtility.getLabel(this.selected, this.getTranslations());

		return this.getTranslations()
			.pickingListSortAriaLabel.replace('[[sort_option]]', sortOptionLabel)
			.replace('{{sort_option}}', sortOptionLabel);
	}

	/**
	 * Get icon for button.
	 *
	 * @param option The option.
	 * @returns Current icon for button.
	 */
	private getIcon(option: SortListOptionsEnum | null): string {
		return option === SortListOptionsEnum.default || option === null
			? 'arrow-up-arrow-down'
			: 'sort-descending';
	}

	private closeSheet(): void {
		if (this.sheetElement) {
			this.sheetElement.open = false;
			this.sheetElement = null;
		}
	}

	private openSheet(): void {
		if (this.sheetElement) {
			return;
		}

		const sheetElement: SkapaSheet = this.createSheetElement();
		this.sheetElement = sheetElement;

		const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
		parent.appendChild(sheetElement);

		sheetElement.open = true;
	}

	private createSheetElement(): SkapaSheet {
		const sheetElement = this.createSheet();
		const sheetHeaderElement = this.createSheetHeader();
		const sortListCardElement = this.createCard();

		sheetElement.appendChild(sheetHeaderElement);
		sheetElement.appendChild(sortListCardElement);
		return sheetElement;
	}

	private createSheet(): SkapaSheet {
		const sheetElement = this.createInternalElement('skapa-sheet');
		sheetElement.setAttribute('aria-label', this.getTranslations().pickingListSortTitle);
		sheetElement.size = 'small';
		sheetElement.fullHeight = true;
		sheetElement.alignToStart = this.sheetAlignment === SheetAlignmentEnum.left;
		sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
		sheetElement.style.zIndex = '500';
		sheetElement.addEventListener('closerequest', () => {
			if (this.sheetElement) {
				this.dispatchEvent(
					new CustomEvent('modalClose', {
						bubbles: true,
						detail: {
							visibleModal: null,
							triggerElement: this.queryTriggerElement()
						}
					})
				);
			}
		});
		sheetElement.addEventListener('closed', () => {
			sheetElement.parentElement?.removeChild(sheetElement);
		});

		return sheetElement;
	}

	private createSheetHeader(): SkapaModalHeader {
		const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
		sheetHeaderElement.slot = 'header';

		const headerSpan = document.createElement('span');
		headerSpan.slot = 'closebutton-label';
		headerSpan.innerText = this.getTranslations().modalAriaLabelButtonClose;

		sheetHeaderElement.appendChild(headerSpan);

		return sheetHeaderElement;
	}

	private createCard(): SortListCard {
		const cardElement = this.createInternalElement('kompis-sort-list-card');

		cardElement.theme = this.theme;
		cardElement.localizedInformation = this.localizedInformation;
		cardElement.translations = this.translations;
		cardElement.selected = this.selected;

		cardElement.addEventListener('change', (event) => {
			this.dispatchEvent(
				new CustomEvent('change', {
					bubbles: true,
					detail: {
						selected: (<CustomEvent>event).detail.selected,
						visibleModal: null,
						triggerElement: this.queryTriggerElement()
					}
				})
			);
		});

		return cardElement;
	}

	/**
	 * Find the sheet trigger element.
	 */
	private queryTriggerElement(): HTMLButtonElement | null {
		return (
			this.shadowRoot
				?.querySelector(this.customElements.getInternalName('skapa-button'))
				?.shadowRoot?.querySelector('button') ?? null
		);
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-sort-list': SortList;
	}
}
